import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext.jsx";
import supabase from "@/services/supabaseClient.js";
import {
  hasCachedCurrentLegalAcceptance,
  hasCurrentLegalAcceptance,
  recordLegalAcceptance,
} from "@/services/legalAcceptanceService.js";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import { recordStagingPerformance } from "@/lib/stagingPerformanceTrace.js";
import {
  LEGAL_ACCEPTANCE_STATUS,
  legalAcceptanceStatusFromCheck,
} from "./legalAcceptanceState.mjs";

function isLegalGateExempt(pathname = "", search = "") {
  const path = String(pathname || "");
  const firstUseCoordinator = new URLSearchParams(String(search || "")).get("first_use") === "1";
  return (
    firstUseCoordinator ||
    path.startsWith("/legal/") ||
    path === "/terms" ||
    path === "/privacy" ||
    path === "/data-deletion" ||
    path === "/login" ||
    path.startsWith("/invite/setup") ||
    path.startsWith("/practice-team/invite") ||
    path.startsWith("/practice-team/accept") ||
    path.startsWith("/practice-team/welcome") ||
    path.startsWith("/auth/callback") ||
    path.startsWith("/admin/auth/callback") ||
    path === "/callback" ||
    path === "/signup" ||
    path.startsWith("/admin/team/accept") ||
    path === "/preferences" ||
    path === "/unsubscribe" ||
    path.startsWith("/communications/") ||
    path.startsWith("/pharmacy/invite")
  );
}

function buildRouteSnapshot(location = {}) {
  const path = `${location.pathname || ""}${location.search || ""}${location.hash || ""}`;
  return path.startsWith("/") && !path.startsWith("//") ? path : "";
}

function traceLegalGate(stage, details = {}) {
  const safeDetails = {
    ...details,
    source: details.source === "cache" ? "cache" : "authoritative",
  };
  // This trace intentionally carries no account identifier, legal content,
  // token, or session information. Staging uses the same bounded in-memory
  // trace as the other authenticated readiness boundaries.
  recordStagingPerformance(`terms_${stage}`, safeDetails);
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info("[LegalAcceptanceGate]", stage, safeDetails);
  }
}

export default function LegalAcceptanceGate({ children }) {
  const auth = useAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = React.useState("checking");
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [checkedUserId, setCheckedUserId] = React.useState("");
  const [retryCount, setRetryCount] = React.useState(0);
  const automaticRetryRef = React.useRef({ userId: "", count: 0 });
  const [resolvedUserId, setResolvedUserId] = React.useState("");

  const userId = String(auth?.user?.id || "").trim();
  const exempt = isLegalGateExempt(location.pathname, location.search);
  const sourceRoute = buildRouteSnapshot(location);
  const allowedFromCache = userId ? hasCachedCurrentLegalAcceptance(userId) : false;

  React.useEffect(() => {
    let alive = true, timer;
    const startedAt = performance.now();
    if (automaticRetryRef.current.userId !== userId) automaticRetryRef.current = { userId, count: 0 };
    const cached = userId ? hasCachedCurrentLegalAcceptance(userId) : false;
    if (exempt || !userId) return;
    // Legal truth needs an authenticated session, not an avatar/profile refresh.
    if (auth?.loading) {
      traceLegalGate("check_waiting_for_auth", { source: "authoritative", authLoading: true });
      return;
    }
    setResolvedUserId(userId);
    setStatus(cached ? "allowed" : "checking");
    traceLegalGate("check_start", { source: "authoritative", background: cached, retryCount: automaticRetryRef.current.count });
    void hasCurrentLegalAcceptance(userId, { bypassCache: true }).then(accepted => {
      if (!alive) return;
      const nextStatus = legalAcceptanceStatusFromCheck({ accepted });
      setStatus(nextStatus);
      setCheckedUserId(accepted ? userId : "");
      traceLegalGate("check_completion", { source: "authoritative", durationMs: Math.round(performance.now() - startedAt), finalState: nextStatus, background: cached });
    }).catch(() => {
      if (!alive) return;
      // Failure is unknown, never a request to reaccept already accepted terms.
      const stillAccepted = hasCachedCurrentLegalAcceptance(userId);
      traceLegalGate("check_completion", { source: "authoritative", durationMs: Math.round(performance.now() - startedAt), finalState: stillAccepted ? "allowed" : "unknown", outcome: "transient_failure" });
      if (automaticRetryRef.current.count < 2) {
        const delay = [500, 1500][automaticRetryRef.current.count++];
        timer = window.setTimeout(() => { if (alive) setRetryCount(count => count + 1); }, delay);
      } else {
        setStatus(stillAccepted ? "allowed" : LEGAL_ACCEPTANCE_STATUS.UNKNOWN);
        traceLegalGate("check_retry_exhausted", { source: "authoritative", finalState: stillAccepted ? "allowed" : "unknown" });
      }
    });
    return () => { alive = false; window.clearTimeout(timer); };
  }, [auth?.loading, exempt, retryCount, userId]);

  function handleVerificationRetry() {
    if (!userId || busy) return;
    automaticRetryRef.current = { userId, count: 0 };
    setError("");
    setRetryCount(count => count + 1);
  }

  async function handleAccept() {
    if (!userId || busy) return;
    setBusy("accept");
    setError("");

    try {
      await recordLegalAcceptance({
        userId,
        sourceRoute,
        accepted: true,
      });
      setCheckedUserId(userId);
      setResolvedUserId(userId);
      setStatus("allowed");
    } catch (nextError) {
      setError(
        nextError?.message ||
          "AfriMed™ could not save your Terms acceptance. Please try again.",
      );
    } finally {
      setBusy("");
    }
  }

  async function handleDecline() {
    if (!userId || busy) return;
    setBusy("decline");
    setError("");

    try {
      await recordLegalAcceptance({
        userId,
        sourceRoute,
        accepted: false,
      });
    } catch (nextError) {
      setError(
        nextError?.message ||
          "AfriMed™ could not save your Terms decision. Please try again.",
      );
      setBusy("");
      return;
    }

    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      // Continue to login even if local sign-out reports a transient error.
    }

    setBusy("");
    navigate("/login?legal=declined", { replace: true });
  }

  const resolvedForUser = resolvedUserId === userId;
  const currentDecision = resolvedForUser ? status : "checking";
  const knownAccepted = allowedFromCache || (resolvedForUser && checkedUserId === userId);
  if (exempt || !userId || currentDecision === "allowed" || (knownAccepted && currentDecision !== LEGAL_ACCEPTANCE_STATUS.REQUIRED)) return children;
  if (currentDecision === "checking") return <RouteFallback label="Checking Terms..." />;

  if (currentDecision === LEGAL_ACCEPTANCE_STATUS.UNKNOWN) {
    return (
      <main className="auth-bg min-h-[100dvh] w-full overflow-hidden px-4 text-[rgb(var(--clr-on-surface))]">
        <div className="grid h-[100dvh] place-items-center overflow-y-auto overscroll-y-contain py-8">
          <section
            aria-labelledby="legal-checking-title"
            className="w-full max-w-[375px] text-center"
          >
            <img
              src="/assets/images/afrimed_logo_stack.svg"
              alt="AfriMed™"
              className="mx-auto mb-10 h-auto w-[135px]"
              draggable="false"
            />
            <h1 id="legal-checking-title" className="mb-3 text-title-large">
              Terms & Privacy
            </h1>
            <p className="mb-7 text-body-large text-on-surface-var">
              We&apos;re still securely checking your Terms &amp; Privacy status.
              Your access has not been changed.
            </p>
            <button
              type="button"
              onClick={handleVerificationRetry}
              className="h-12 w-full rounded-[12px] bg-[rgb(var(--afrimed-primary))] px-5 text-title-medium font-medium text-[rgb(var(--afrimed-on-primary))] transition hover:opacity-95"
            >
              Retry check
            </button>
          </section>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-bg min-h-[100dvh] w-full overflow-hidden px-4 text-[rgb(var(--clr-on-surface))]">
      <div className="grid h-[100dvh] place-items-center overflow-y-auto overscroll-y-contain py-8">
        <section
          aria-labelledby="legal-acceptance-title"
          className="w-full max-w-[375px] text-center"
        >
          <img
            src="/assets/images/afrimed_logo_stack.svg"
            alt="AfriMed™"
            className="mx-auto mb-10 h-auto w-[135px]"
            draggable="false"
          />

          <h1 id="legal-acceptance-title" className="mb-3 text-title-large">
            Terms & Privacy
          </h1>
          <p className="mb-7 text-body-large text-on-surface-var">
            Before continuing, please review and accept the AfriMed™ Terms &
            Conditions and Privacy Policy. These protect patients, clinicians,
            and the platform.
          </p>

          <div className="mb-6 grid gap-3">
            <Link
              className="auth-social-button inline-flex min-h-12 w-full items-center justify-center rounded-[12px] px-5 py-3 text-title-medium no-underline transition"
              to="/legal/terms"
              target="_blank"
              rel="noreferrer"
            >
              <span className="auth-social-label font-medium">
                Terms & Conditions
              </span>
            </Link>
            <Link
              className="auth-social-button inline-flex min-h-12 w-full items-center justify-center rounded-[12px] px-5 py-3 text-title-medium no-underline transition"
              to="/legal/privacy"
              target="_blank"
              rel="noreferrer"
            >
              <span className="auth-social-label font-medium">
                Privacy Policy
              </span>
            </Link>
            <Link
              className="auth-social-button inline-flex min-h-12 w-full items-center justify-center rounded-[12px] px-5 py-3 text-title-medium no-underline transition"
              to="/legal/data-deletion"
              target="_blank"
              rel="noreferrer"
            >
              <span className="auth-social-label font-medium">
                Data Deletion
              </span>
            </Link>
          </div>

          {error ? (
            <div className="mb-5 rounded-[16px] bg-white/75 px-4 py-3 text-body-medium text-[rgb(var(--clr-error))] shadow-[0_8px_24px_rgba(24,39,75,0.08)]">
              {error}
            </div>
          ) : null}

          <div className="grid gap-4">
            <button
              type="button"
              onClick={handleAccept}
              disabled={Boolean(busy)}
              className="h-12 w-full rounded-[12px] bg-[rgb(var(--afrimed-primary))] px-5 text-title-medium font-medium text-[rgb(var(--afrimed-on-primary))] transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-55"
            >
              {busy === "accept" ? "Saving..." : "Agree and continue"}
            </button>
            <button
              type="button"
              onClick={handleDecline}
              disabled={Boolean(busy)}
              className="auth-social-button h-12 w-full rounded-[12px] px-5 text-title-medium transition disabled:cursor-not-allowed disabled:opacity-55"
            >
              <span className="auth-social-label font-medium">
                {busy === "decline" ? "Signing out..." : "I do not agree"}
              </span>
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}
